import React, { useState } from "react";
import styled, { css } from "styled-components";
import { DEFAULT_PROJECT_ID, UNASSIGNED_PROJECT } from "@lifeat/shared/projects/ProjectsConstants";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { FontSize } from "@themes/Fonts";
import { projectList$ } from "../../../features/projects/ProjectGlobals";
import ProjectChip from "./ProjectChip";

const REASSIGN_PROJECT_LABEL = "Reassign project";
const ASSIGN_PROJECT_LABEL = "Assign project";

const StyledProjectChip = styled(ProjectChip)`
    font-size: ${FontSize.normal};
`;

const RadioButtonItemContainer = styled.div`
    display: flex;
    gap: 7px;
`;

const DropdownItemContainer = styled.div<{ isDisabled?: boolean; blockCursor?: boolean }>`
    display: flex;
    width: 100%;
    align-items: center;

    color: ${(props) => (props.isDisabled ? props.theme.textColorSecondary : props.theme.textColorPrimary)};
    opacity: ${(props) => (props.isDisabled ? `0.7` : `1`)};

    font-size: ${FontSize.normal};
    padding: 10px;

    cursor: pointer;

    ${(props) =>
        props.blockCursor &&
        css`
            cursor: not-allowed;
            * {
                cursor: not-allowed;
            }
        `};

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
    }
`;

export function DropdownItem(props: {
    onClick: () => void;
    isDisabled?: boolean;
    blockCursor?: boolean;
    children: React.ReactNode;
}) {
    return (
        <DropdownItemContainer
            isDisabled={props.isDisabled}
            blockCursor={props.blockCursor}
            onClick={(e) => {
                e.stopPropagation();
                props.onClick();
            }}
        >
            {props.children}
        </DropdownItemContainer>
    );
}

type ProjectAssignmentDropdownProps = {
    selectedProjectId?: string;
    updateTaskProject: (projectId: string | null) => Promise<void>;
};
export function ProjectAssignmentDropdown(props: ProjectAssignmentDropdownProps) {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const selectedProject =
        props.selectedProjectId && projectList$.value[props.selectedProjectId]
            ? projectList$.value[props.selectedProjectId]
            : UNASSIGNED_PROJECT;

    const handleProjectSelect = async (input: string) => {
        const nextProjectId = input === DEFAULT_PROJECT_ID ? null : input;
        await props.updateTaskProject(nextProjectId);
        setIsPopoverOpen(false);
    };

    return (
        <DropdownMenuComponents.Root open={isPopoverOpen} onOpenChange={(open: boolean) => setIsPopoverOpen(open)}>
            <DropdownMenuComponents.Trigger>
                <StyledProjectChip project={selectedProject} />
            </DropdownMenuComponents.Trigger>
            <DropdownMenuComponents.Content>
                <DropdownMenuComponents.HeaderItem
                    label={props.selectedProjectId ? REASSIGN_PROJECT_LABEL : ASSIGN_PROJECT_LABEL}
                />
                {Object.entries(projectList$.value).map(([projectId, project]) => (
                    <DropdownItem key={projectId} onClick={() => handleProjectSelect(projectId)}>
                        <RadioButtonItemContainer>
                            <input
                                type="radio"
                                readOnly
                                value={projectId}
                                checked={projectId === props.selectedProjectId}
                                id={`project-assignment-option-${projectId}`}
                            />
                            <label htmlFor={`project-assignment-option-${projectId}`}>
                                <ProjectChip project={project} disableHover={true} />
                            </label>
                        </RadioButtonItemContainer>
                    </DropdownItem>
                ))}
                <DropdownItem key={DEFAULT_PROJECT_ID} onClick={() => handleProjectSelect(DEFAULT_PROJECT_ID)}>
                    <RadioButtonItemContainer>
                        <input
                            type="radio"
                            readOnly
                            value={DEFAULT_PROJECT_ID}
                            checked={DEFAULT_PROJECT_ID === props.selectedProjectId}
                            id={`tag-assignment-option-default-tag`}
                        />
                        <label htmlFor={`tag-assignment-option-default-tag`}>
                            <ProjectChip project={UNASSIGNED_PROJECT} disableHover={true} />
                        </label>
                    </RadioButtonItemContainer>
                </DropdownItem>
                <DropdownMenuComponents.Separator />
            </DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );
}
